import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { DataLabel, LANG_LABEL } from "../components/common";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FloppyDisk } from "@phosphor-icons/react";

const MODELS = [
  ["openai", "gpt-5.4", "GPT-5.4 (OpenAI)"],
  ["openai", "gpt-5.4-mini", "GPT-5.4 mini (OpenAI)"],
  ["anthropic", "claude-sonnet-4-6", "Claude Sonnet 4.6 (Anthropic)"],
  ["gemini", "gemini-3.1-pro-preview", "Gemini 3.1 Pro (Google)"],
];

const WEEKDAYS = [[0, "Lundi"], [1, "Mardi"], [2, "Mercredi"], [3, "Jeudi"], [4, "Vendredi"], [5, "Samedi"], [6, "Dimanche"]];

export default function Strategy() {
  const [s, setS] = useState(null);
  const [tax, setTax] = useState({ universes: [], content_types: [], languages: [] });

  useEffect(() => {
    api.get("/strategy").then((r) => setS(r.data));
    api.get("/config/taxonomy").then((r) => setTax(r.data));
  }, []);

  if (!s) return <div className="font-mono text-xs text-muted-foreground">Chargement…</div>;

  const set = (patch) => setS({ ...s, ...patch });
  const toggleArr = (field, val) => {
    const arr = s[field] || [];
    set({ [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const save = async () => {
    try {
      const { data } = await api.put("/strategy", {
        languages: s.languages, primary_language: s.primary_language,
        publish_frequency_per_day: Number(s.publish_frequency_per_day),
        auto_publish: s.auto_publish, auto_publish_types: s.auto_publish_types,
        min_relevance_score: Number(s.min_relevance_score),
        pubmed_queries: s.pubmed_queries, generation_provider: s.generation_provider,
        generation_model: s.generation_model, active_universes: s.active_universes,
        sources_enabled: s.sources_enabled, rss_feeds: s.rss_feeds,
        auto_insert_links: s.auto_insert_links, auto_tune: s.auto_tune,
        auto_deploy: s.auto_deploy, min_references: Number(s.min_references),
        notifications: s.notifications, schedule: s.schedule,
      });
      setS(data); toast.success("Stratégie enregistrée");
    } catch (e) { toast.error("Échec de l'enregistrement"); }
  };

  const modelValue = `${s.generation_provider}|${s.generation_model}`;
  const notif = s.notifications || {};
  const setNotif = (patch) => set({ notifications: { ...notif, ...patch } });
  const sched = s.schedule || {};
  const setSched = (patch) => set({ schedule: { ...sched, ...patch } });
  const testEmail = async () => {
    try {
      const { data } = await api.post("/notifications/test");
      if (data.status === "sent") toast.success("E-mail de test envoyé ✔");
      else if (data.status === "no_key") toast.warning("Clé API Resend manquante", { description: "Ajoutez RESEND_API_KEY pour activer l'envoi." });
      else if (data.status === "no_recipient") toast.warning("Renseignez l'e-mail destinataire puis enregistrez.");
      else toast.error("Échec d'envoi", { description: data.error?.slice(0, 120) });
    } catch (e) { toast.error("Impossible d'envoyer le test"); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Configuration</div>
          <h1 className="font-serif text-4xl text-brand tracking-tight">Stratégie éditoriale</h1>
        </div>
        <button data-testid="save-strategy-btn" onClick={save}
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
          <FloppyDisk size={16} /> Enregistrer
        </button>
      </div>

      <div className="space-y-5">
        {/* Languages */}
        <section className="bg-white border border-border rounded-md p-6">
          <DataLabel>Langues de publication</DataLabel>
          <div className="flex flex-wrap gap-2 mt-3">
            {tax.languages.map((l) => (
              <button key={l} data-testid={`lang-toggle-${l}`} onClick={() => toggleArr("languages", l)}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                  s.languages.includes(l) ? "bg-brand text-white border-brand" : "border-border hover:bg-secondary"}`}>
                {LANG_LABEL[l] || l}</button>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Langue principale</span>
            <Select value={s.primary_language} onValueChange={(v) => set({ primary_language: v })}>
              <SelectTrigger data-testid="primary-lang" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{s.languages.map((l) => <SelectItem key={l} value={l}>{LANG_LABEL[l] || l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </section>

        {/* Automation */}
        <section className="bg-white border border-border rounded-md p-6">
          <DataLabel>Automatisation & publication</DataLabel>
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div>
              <label className="text-sm">Publications / jour</label>
              <input data-testid="freq-input" type="number" min={0} max={10} value={s.publish_frequency_per_day}
                onChange={(e) => set({ publish_frequency_per_day: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md border border-border text-sm" />
            </div>
            <div>
              <label className="text-sm">Score de pertinence minimum (veille)</label>
              <input data-testid="minscore-input" type="number" min={0} max={100} value={s.min_relevance_score}
                onChange={(e) => set({ min_relevance_score: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md border border-border text-sm" />
            </div>
            <div>
              <label className="text-sm">Nombre minimum de références par article</label>
              <select data-testid="min-references-select" value={s.min_references || 6}
                onChange={(e) => set({ min_references: Number(e.target.value) })}
                className="w-full mt-1 px-3 py-2 rounded-md border border-border text-sm bg-white">
                {[3, 5, 6, 8, 10, 12].map((n) => <option key={n} value={n}>{n} références</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Références scientifiques réelles (PubMed / revues) citées et listées en bibliographie.</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-5 pt-5 border-t border-border">
            <div>
              <div className="text-sm font-medium">Publication automatique</div>
              <div className="text-xs text-muted-foreground">Publie sans validation pour les types cochés ci-dessous. Déconseillé pour le contenu médical.</div>
            </div>
            <Switch data-testid="auto-publish-switch" checked={s.auto_publish} onCheckedChange={(v) => set({ auto_publish: v })} />
          </div>
          {s.auto_publish && (
            <div className="flex flex-wrap gap-2 mt-4">
              {tax.content_types.map((t) => (
                <button key={t.key} onClick={() => toggleArr("auto_publish_types", t.key)}
                  className={`px-2.5 py-1 rounded text-xs border transition-colors ${
                    (s.auto_publish_types || []).includes(t.key) ? "bg-ai text-white border-ai" : "border-border hover:bg-secondary"}`}>
                  {t.label}</button>
              ))}
            </div>
          )}
        </section>

        {/* Scientific sources */}
        <section className="bg-white border border-border rounded-md p-6">
          <DataLabel>Sources scientifiques</DataLabel>
          <div className="grid md:grid-cols-3 gap-3 mt-4">
            {[["pubmed", "PubMed"], ["europepmc", "Europe PMC"], ["clinicaltrials", "ClinicalTrials.gov"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between border border-border rounded-md px-3 py-2.5">
                <span className="text-sm">{l}</span>
                <Switch data-testid={`source-toggle-${k}`}
                  checked={(s.sources_enabled || {})[k] !== false}
                  onCheckedChange={(v) => set({ sources_enabled: { ...(s.sources_enabled || {}), [k]: v } })} />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <label className="text-sm">Flux RSS (Cochrane, sociétés savantes AAD/EADV/SEME/BAD…) — un par ligne</label>
            <textarea data-testid="rss-feeds" rows={3} value={(s.rss_feeds || []).join("\n")}
              onChange={(e) => set({ rss_feeds: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
              placeholder="https://www.cochranelibrary.com/rss/...&#10;https://www.aad.org/rss/..."
              className="w-full mt-1 px-3 py-2 rounded-md border border-border text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
        </section>

        {/* Autonomy behaviours */}
        <section className="bg-white border border-border rounded-md p-6 space-y-4">
          <DataLabel>Comportements autonomes</DataLabel>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Insertion automatique du maillage interne</div>
              <div className="text-xs text-muted-foreground">Insère les liens internes pertinents dans le corps du texte à la publication.</div>
            </div>
            <Switch data-testid="auto-insert-switch" checked={!!s.auto_insert_links} onCheckedChange={(v) => set({ auto_insert_links: v })} />
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              <div className="text-sm font-medium">Auto-ajustement de la stratégie (apprentissage)</div>
              <div className="text-xs text-muted-foreground">Ajuste automatiquement fréquence et priorités selon les performances (Search Console/Bing/Analytics).</div>
            </div>
            <Switch data-testid="auto-tune-switch" checked={!!s.auto_tune} onCheckedChange={(v) => set({ auto_tune: v })} />
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              <div className="text-sm font-medium">Auto-déploiement après validation</div>
              <div className="text-xs text-muted-foreground">Dès qu'un article est validé médicalement, il est mis en ligne automatiquement sur le site (aucun clic « Déclencher le déploiement »). La validation médicale reste obligatoire.</div>
            </div>
            <Switch data-testid="auto-deploy-switch" checked={s.auto_deploy !== false} onCheckedChange={(v) => set({ auto_deploy: v })} />
          </div>
        </section>

        {/* Schedule */}
        <section className="bg-white border border-border rounded-md p-6">
          <DataLabel>Horaires automatiques (heure de Madrid)</DataLabel>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {[["cycle_hour", "Cycle autonome (veille + génération)", 5],
              ["digest_hour", "Récapitulatif quotidien", 7],
              ["reminder_hour", "Rappels", 8]].map(([k, label, def_]) => (
              <div key={k}>
                <label className="text-sm">{label}</label>
                <div className="flex items-center gap-2 mt-1">
                  <input data-testid={`sched-${k}`} type="number" min={0} max={23}
                    value={sched[k] ?? def_} onChange={(e) => setSched({ [k]: Number(e.target.value) })}
                    className="w-20 px-3 py-2 rounded-md border border-border text-sm" />
                  <span className="text-xs text-muted-foreground">h00</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-border">
            <label className="text-sm font-medium">Résumé hebdomadaire par e-mail</label>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Envoi chaque</span>
              <select data-testid="weekly-day-select" value={sched.weekly_day ?? 0}
                onChange={(e) => setSched({ weekly_day: Number(e.target.value) })}
                className="px-3 py-2 rounded-md border border-border text-sm bg-white">
                {WEEKDAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <span className="text-xs text-muted-foreground">à</span>
              <input data-testid="weekly-hour-input" type="number" min={0} max={23}
                value={sched.weekly_hour ?? 7} onChange={(e) => setSched({ weekly_hour: Number(e.target.value) })}
                className="w-20 px-3 py-2 rounded-md border border-border text-sm" />
              <span className="text-xs text-muted-foreground">h00 (heure de Madrid)</span>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section className="bg-white border border-border rounded-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <DataLabel>Notifications de validation</DataLabel>
            <button data-testid="test-email-btn" onClick={testEmail}
              className="text-xs border border-brand text-brand hover:bg-brand-soft px-3 py-1.5 rounded-md transition-colors">
              Envoyer un e-mail de test
            </button>
          </div>
          <div>
            <label className="text-sm">E-mail destinataire</label>
            <input data-testid="notif-email" type="email" value={notif.email || ""}
              onChange={(e) => setNotif({ email: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-md border border-border text-sm font-mono" />
          </div>
          <div className="flex items-center justify-between pt-2">
            <div>
              <div className="text-sm font-medium">Notification immédiate</div>
              <div className="text-xs text-muted-foreground">Un e-mail dès qu'un contenu est prêt à relire.</div>
            </div>
            <Switch data-testid="notif-immediate-switch" checked={notif.immediate !== false} onCheckedChange={(v) => setNotif({ immediate: v })} />
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              <div className="text-sm font-medium">Récapitulatif quotidien</div>
              <div className="text-xs text-muted-foreground">Un résumé quotidien (07:00 UTC) des contenus en attente.</div>
            </div>
            <Switch data-testid="notif-digest-switch" checked={notif.daily_digest !== false} onCheckedChange={(v) => setNotif({ daily_digest: v })} />
          </div>
          <div className="pt-2 border-t border-border">
            <label className="text-sm">Rappel automatique après (jours sans validation) — 0 pour désactiver</label>
            <input data-testid="notif-reminder-days" type="number" min={0} max={30} value={notif.reminder_days ?? 3}
              onChange={(e) => setNotif({ reminder_days: Number(e.target.value) })}
              className="w-32 mt-1 px-3 py-2 rounded-md border border-border text-sm" />
          </div>
        </section>

        {/* Model */}
        <section className="bg-white border border-border rounded-md p-6">
          <DataLabel>Modèle de génération IA</DataLabel>
          <Select value={modelValue} onValueChange={(v) => { const [p, m] = v.split("|"); set({ generation_provider: p, generation_model: m }); }}>
            <SelectTrigger data-testid="model-select" className="w-full mt-3"><SelectValue /></SelectTrigger>
            <SelectContent>{MODELS.map(([p, m, l]) => <SelectItem key={`${p}|${m}`} value={`${p}|${m}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </section>

        {/* Universes + PubMed queries */}
        <section className="bg-white border border-border rounded-md p-6">
          <DataLabel>Univers médicaux & requêtes de veille PubMed</DataLabel>
          <div className="space-y-3 mt-4">
            {tax.universes.map((u) => {
              const active = (s.active_universes || []).includes(u.key);
              return (
                <div key={u.key} className="border border-border rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${active ? "" : "text-muted-foreground"}`}>{u.label}</span>
                    <Switch data-testid={`uni-toggle-${u.key}`} checked={active} onCheckedChange={() => toggleArr("active_universes", u.key)} />
                  </div>
                  <input value={s.pubmed_queries?.[u.key] || ""}
                    onChange={(e) => set({ pubmed_queries: { ...s.pubmed_queries, [u.key]: e.target.value } })}
                    className="w-full mt-2 px-2.5 py-1.5 rounded-md border border-border text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-brand/30" />
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
